import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Globe, Clock, Package, ShieldCheck } from 'lucide-react';
import { PageFAQSchema, useCanonicalUrl } from '../components/seo/SchemaMarkup';

const SHIPPING_FAQS = [
  {
    question: 'How long does shipping take?',
    answer: 'Standard US orders ship in 1-3 business days and arrive within 3-7 business days. Expedited orders arrive within 2-3 business days. International orders typically take 7-14 business days depending on destination and customs.',
  },
  {
    question: 'How much does shipping cost?',
    answer: 'Standard US shipping is $4.99 flat rate, or FREE on orders over $75. Expedited US shipping starts at $14.99. International shipping is calculated at checkout based on weight and destination — orders over $150 ship free to Canada, UK, and Australia.',
  },
  {
    question: 'Do you ship internationally?',
    answer: 'Yes. NinjAthlete ships to the US, Canada, UK, Australia, New Zealand, and most of the EU. Duties and import taxes are the responsibility of the buyer and are not included in the checkout total.',
  },
  {
    question: 'How can I track my order?',
    answer: 'Once your order ships you will receive a confirmation email with a tracking number and link. You can also view real-time tracking from your account at ninjathlete.com/account/orders.',
  },
  {
    question: 'When does my order ship?',
    answer: 'Orders placed before 12:00 PM CT on a business day ship the same day. Orders placed after that cutoff (or on weekends and holidays) ship the next business day.',
  },
  {
    question: 'Do digital products require shipping?',
    answer: 'No. Digital products (training plans, e-books, PDFs) are delivered instantly via email and your account dashboard — no shipping required and no shipping fees applied.',
  },
];

const SHIPPING_HIGHLIGHTS = [
  { icon: Truck, title: 'Free over $75', desc: 'Free standard US shipping on every order over $75 — no code required.' },
  { icon: Clock, title: '1-3 day handling', desc: 'Orders placed before 12pm CT ship the same business day from our Dallas, TX warehouse.' },
  { icon: Globe, title: 'Worldwide shipping', desc: 'We ship to the US, Canada, UK, Australia, New Zealand, and most of the EU.' },
  { icon: Package, title: 'Real-time tracking', desc: 'Tracking link delivered the moment your label is scanned by the carrier.' },
  { icon: ShieldCheck, title: 'Carbon-neutral', desc: 'Every package ships in 100% recyclable polymailers with carbon-neutral last-mile delivery.' },
];

export default function ShippingPage() {
  useCanonicalUrl();
  return (
    <main className="pt-20 min-h-screen">
      <PageFAQSchema
        faqs={SHIPPING_FAQS}
        pageName="NinjAthlete Shipping Policy"
        pagePath="/shipping"
        scriptId="shipping-faq-schema"
      />
      <div className="container-ninja section-spacing max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
            <Truck className="w-8 h-8 text-blue-400" />
          </div>
          <h1
            className="text-4xl sm:text-5xl uppercase tracking-wider text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="shipping-heading"
          >
            Shipping
          </h1>
          <p className="text-zinc-400 text-base max-w-lg mx-auto">
            Fast, tracked, and carbon-neutral. Standard US shipping is free over $75, and we ship worldwide to athletes in the US, Canada, UK, EU, Australia, and New Zealand.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {SHIPPING_HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-zinc-900/60 border border-zinc-800 p-6">
              <Icon className="w-5 h-5 text-blue-400 mb-3" />
              <h3 className="text-white text-sm font-semibold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{title}</h3>
              <p className="text-zinc-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <section className="border-t border-zinc-900 pt-10" data-testid="shipping-faq">
          <h2
            className="text-2xl uppercase tracking-wider text-white mb-6 text-center"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Shipping FAQs
          </h2>
          <div className="space-y-2">
            {SHIPPING_FAQS.map((faq, i) => (
              <details
                key={faq.q}
                className="group border border-zinc-800 bg-zinc-900/40"
                data-testid={`shipping-faq-item-${i}`}
              >
                <summary className="cursor-pointer px-5 py-4 text-sm text-zinc-200 font-medium flex items-center justify-between hover:bg-zinc-900/70 transition-colors">
                  <span>{faq.question}</span>
                  <span className="text-zinc-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <div className="px-5 pb-4 text-zinc-400 text-sm leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        <div className="text-center text-zinc-600 text-xs mt-10">
          Questions about shipping? <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors underline">Contact us</Link>
        </div>
      </div>
    </main>
  );
}
